import {
  MODULE_DIRECTION_STRAIGHT,
  MODULE_FAMILY_CORRIDOR,
  MODULE_FAMILY_ROOM,
  getPickableElements,
  hasBuiltGeometry,
  type MapEditorCatalogElement,
} from './mapEditorCatalog';

// Whole walks an author can drop in with one click, offered while the layout is still empty.
//
// An empty museum is the one moment an author has nothing to react to: every other state of this
// editor is a change to something already on screen. Three starters are enough to make the first
// move a choice between shapes rather than a blank list of 39 module names, and everything is
// editable the instant it lands — a starter is a first draft, not a template the layout stays inside.
//
// Resolved against the catalog rather than shipped as ids. The design was drawn against one kit and
// the hub serves every build: a starter naming `corridor-1a-long-left` would silently stop working
// on the first map whose kit renames it, and would go on offering a card that adds nothing. So a
// starter is a shape — "a medium room, a corridor that turns, a large room, a corridor that turns" —
// and a build that cannot supply one of its steps does not offer that starter at all.

// One step of a walk: what kind of module, and which of its variants would do.
//
// `direction` is the field that carries the shape. `Short loop` turns back on itself, which is four
// left turns; relaxing that to "any corridor" would put a straight walk behind a card whose picture
// shows a loop. So a step names it when it matters and leaves it open when it does not, and a step
// that cannot be filled is what stops the whole starter being offered.
interface StarterStep {
  family: string;
  // Preferred sizes, best first. A build shipping none of them fills the step with any module of the
  // right family and direction — a starter of the right shape at the wrong scale is still the right
  // first draft, where no starter at all is not.
  sizes: string[];
  direction?: string;
}

interface StarterRecipe {
  id: string;
  // Translation keys, resolved by the caller. The name and the line under it are prose about a shape
  // rather than about the modules that happen to fill it, so they are written here rather than
  // assembled from the resolved chain.
  nameKey: string;
  nameFallback: string;
  hintKey: string;
  hintFallback: string;
  steps: StarterStep[];
}

const room = (sizes: string[]): StarterStep => ({
  family: MODULE_FAMILY_ROOM,
  sizes,
  direction: MODULE_DIRECTION_STRAIGHT,
});

const turningCorridor = (direction: string): StarterStep => ({
  family: MODULE_FAMILY_CORRIDOR,
  sizes: ['L', 'M', 'S'],
  direction,
});

const straightCorridor = (): StarterStep => ({
  family: MODULE_FAMILY_CORRIDOR,
  sizes: ['L', 'M', 'S'],
  direction: MODULE_DIRECTION_STRAIGHT,
});

const STARTER_RECIPES: StarterRecipe[] = [
  {
    id: 'short-loop',
    nameKey: 'editorMode.mapEditorStarterLoop',
    nameFallback: 'Short loop',
    hintKey: 'editorMode.mapEditorStarterLoopHint',
    hintFallback: '4 segments · turns back on itself',
    steps: [
      room(['M']),
      turningCorridor('left'),
      room(['L', 'M']),
      turningCorridor('left'),
    ],
  },
  {
    id: 'gallery-run',
    nameKey: 'editorMode.mapEditorStarterRun',
    nameFallback: 'Long gallery run',
    hintKey: 'editorMode.mapEditorStarterRunHint',
    hintFallback: '7 segments · one straight walk',
    steps: [
      room(['M']),
      straightCorridor(),
      room(['L', 'M']),
      straightCorridor(),
      room(['M']),
      straightCorridor(),
      room(['L', 'M']),
    ],
  },
  {
    id: 'single-hall',
    nameKey: 'editorMode.mapEditorStarterHall',
    nameFallback: 'Single hall',
    hintKey: 'editorMode.mapEditorStarterHallHint',
    hintFallback: '1 segment · nothing but the art',
    steps: [room(['L', 'M', 'S'])],
  },
];

export interface MapEditorStarter {
  id: string;
  nameKey: string;
  nameFallback: string;
  hintKey: string;
  hintFallback: string;
  // The modules this build fills the shape with, in order. Never empty: a recipe that could not be
  // filled is not returned at all.
  elements: MapEditorCatalogElement[];
}

/**
 * Fills one step of a recipe.
 *
 * Prefers a module whose geometry this build can actually put on the ground: a starter exists to give
 * the author something standing, and a chain of grey boxes is the one outcome worse than an empty
 * stage. Falls back to a greybox module rather than dropping the starter, because a kit part-way
 * through its art still lays out.
 *
 * @param elements - Modules to choose from.
 * @param step - What the step asks for.
 * @returns The module, or undefined when nothing of that family and direction is shipped.
 */
const fillStep = (
  elements: MapEditorCatalogElement[],
  step: StarterStep,
): MapEditorCatalogElement | undefined => {
  const eligible = elements.filter(
    (element) =>
      element.family === step.family &&
      (step.direction === undefined || element.direction === step.direction),
  );
  if (!eligible.length) {
    return undefined;
  }

  const built = eligible.filter(hasBuiltGeometry);
  const preferred = built.length ? built : eligible;

  const bySize = step.sizes
    .map((size) => preferred.find((element) => element.size === size))
    .find((element) => !!element);

  return bySize ?? preferred[0];
};

/**
 * The starter walks this build can offer.
 *
 * @param elements - The resolved catalog.
 * @returns One entry per recipe the kit can fill, in the order they are offered. Empty for a kit with
 * no corridors or no rooms, where every recipe but the single hall falls away — which the caller
 * shows as no starters rather than as one.
 */
export const resolveStarters = (
  elements: MapEditorCatalogElement[],
): MapEditorStarter[] => {
  const pickable = getPickableElements(elements);

  return STARTER_RECIPES.reduce<MapEditorStarter[]>((starters, recipe) => {
    const filled = recipe.steps.map((step) => fillStep(pickable, step));
    if (filled.some((element) => !element)) {
      return starters;
    }

    return [
      ...starters,
      {
        id: recipe.id,
        nameKey: recipe.nameKey,
        nameFallback: recipe.nameFallback,
        hintKey: recipe.hintKey,
        hintFallback: recipe.hintFallback,
        elements: filled as MapEditorCatalogElement[],
      },
    ];
  }, []);
};
